import React, { useState, useMemo, useEffect } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import axios from 'axios';
import { getAPIUrl } from "../api/apiUtils";
import './TextAnomalityAnalysis.scss';
import './Sidebar.scss';
import ExportModal from '../components/ExportModal';
import DataWindowSlider from '../components/DataWindowSlider';
import { getGroupTitleById, groupIdToTitle } from '../utils/groupTitles';
import PostDetailsModal from '../components/PostDetailsModal';
import { getAnomalityMarkerLabel, anomalityMarkerCategories, isNumericAnomalityMarker, 
  isAnomalityMarkerHidden, anomalityMarkersDictionary, isAnomalityMarkerSystem } from './anomalityMarkersDictionary';
import { useGlobalFilters, useGlobalFiltersForAPI } from '../hooks/useGlobalFilters';
import { debounce } from '../utils/storeUtils';
import ComparisonChart from '../components/ComparisonChart';
import Pagination from '../components/Pagination';
import { useDataWindow } from '../hooks/useDataWindow';
import { fillDataGaps, getActualDataLength } from '../utils/dataUtils';
import { formatRuDate } from '../utils/ruDateUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const API_BASE_URL = getAPIUrl();

const columnHelper = createColumnHelper();

const selectedMarkers = ['text_anomality', 'group_id', 'post_id', 'date', 'time_stamp', 'session_id'];

// Расширенный словарь для этого компонента (добавляем значение "0")
const extendedGroupIdToTitle = {
  ...groupIdToTitle,
  "0": "Среднее по сообществам"
};

const TextAnomalityAnalysis = observer(() => {
  const filtersStore = useGlobalFilters();
  console.log('filtersstore: ', filtersStore)
  const apiFilters = useGlobalFiltersForAPI();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(true);
  const [viewMode, setViewMode] = useState('chart'); // 'table' или 'chart'
  const [aggregation, setAggregation] = useState('1'); // 'day' | 'week' | 'month'
  // Новый стейт для данных графика
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Обработка данных с заполнением пропусков
  const processedChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    const { startDate, endDate } = filtersStore.filters;
    if (!startDate || !endDate) {
      return chartData;
    }

    return fillDataGaps(
      chartData, 
      startDate, 
      endDate, 
      aggregation
    );
  }, [chartData, filtersStore.filters, aggregation]);
  
  const [dataWindow, updateDataWindow] = useDataWindow(100, getActualDataLength(processedChartData));

  // Состояния для модального окна
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Категории маркеров (для обратной совместимости с существующим кодом)
  const categories = useMemo(() => {
    const categoriesObj = {};
    anomalityMarkerCategories.forEach(cat => {
      categoriesObj[cat.category] = cat.markers;
    });
    return categoriesObj;
  }, []);

  const allColumnsFlat = useMemo(
    () => Object.values(categories).flat().map(key =>
      columnHelper.accessor(key, {
        header: getAnomalityMarkerLabel(key),
        cell: info => {
          if (key === 'time_stamp') { // time_stamp is date
            return new Date(info.getValue()).toLocaleString('ru-RU');
          }
          if (key === 'date') { // date is unix timestamp
            return new Date(info.getValue() * 1000).toLocaleString('ru-RU');
          }
          if (key === 'group_id') {
            return getGroupTitleById(info.getValue());
          }
          if (key === 'post_id') {
            return info.getValue();
          }
          const value = info.getValue();
          return isNumericAnomalityMarker(key) && value != null && !isNaN(value) ? Number(value).toFixed(3) : value;
        }
      })
    ),
    [categories]
  );
  
  // Создаем структуру, ожидаемую Sidebar
  const allCategoriesForSidebar = useMemo(() => {
    return Object.entries(categories).map(([categoryName, columnKeys]) => ({
      category: categoryName,
      columns: allColumnsFlat.filter(col => columnKeys.includes(col.accessorKey)),
    }));
  }, [categories, allColumnsFlat]);

  const columns = useMemo(
    () => {
      console.log('columns', selectedMarkers);
      // Всегда скрытые столбцы
      const ALWAYS_HIDDEN = new Set(['time_stamp', 'session_id']);
      // Выбираем все маркеры, которые входят в selectedMarkers (кроме ALWAYS_HIDDEN)
      // и не системные скрытые по словарю (тоже исключая ALWAYS_HIDDEN)
      const visibleMarkers = [
        ...selectedMarkers.filter((key) => !ALWAYS_HIDDEN.has(key)),
        ...Object.keys(anomalityMarkersDictionary).filter(
          key => isAnomalityMarkerHidden(key) && !isAnomalityMarkerSystem(key) && !ALWAYS_HIDDEN.has(key) && !selectedMarkers.includes(key)
        )
      ];

      return visibleMarkers.length > 0 ? allColumnsFlat.filter(col => visibleMarkers.includes(col.accessorKey)) : [];
    },
    [selectedMarkers, allColumnsFlat]
  );

  // Используем фильтры из store вместо прямого обращения к localStorage


  // Обработчик клика по строке таблицы
  const handleRowClick = (row) => {
    if (row.group_id && row.post_id) {
      setSelectedRow(row);
      setIsModalOpen(true);
    }
  };

  

  // Debounced функция для загрузки данных
  const debouncedFetchData = useMemo(
    () => debounce(async (filters, pageIndex = 0, pageSize = 10, sorting = []) => {
      setLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams();
        
        // Добавляем параметры фильтрации
        if (filters.selectedGroups && filters.selectedGroups.length > 0) {
          params.append('group_ids', filters.selectedGroups.join(','));
        }
        if (filters.startDate) {
          params.append('start_date', filters.startDate);
        }
        if (filters.endDate) {
          params.append('end_date', filters.endDate);
        }
        if (filters.minLikes) {
          params.append('min_likes', filters.minLikes);
        }
        if (filters.minViews) {
          params.append('min_views', filters.minViews);
        }
        if (filters.minComments) {
          params.append('min_comments', filters.minComments);
        }
        
        // Добавляем параметры пагинации
        params.append('page', pageIndex + 1);
        params.append('page_size', pageSize);
        
        // Добавляем параметры сортировки
        if (sorting.length > 0) {
          const sort = sorting[0];
          params.append('sort_by', sort.id);
          params.append('sort_order', sort.desc ? 'desc' : 'asc');
        }
        console.log("PARAMS:", params.toString());

        const response = await axios.get(`${API_BASE_URL}/detection/text_anomality_analysis?${params}`);
        const result = response.data;
        
        setData(result.data || []);
        setPageCount(Math.ceil((result.total || 0) / pageSize));
        setTotalRows(result.total || 0);
      } catch (err) {
        setError(err.message);
        console.error('Ошибка загрузки данных:', err);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Загрузка данных при изменении фильтров
  useEffect(() => {
    if (filtersStore.selectedGroups.length > 0) {
      debouncedFetchData(filtersStore.filters);
    } else {
      setData([]);
      setPageCount(1);
      setTotalRows(0);
    }
  }, [filtersStore.filters, debouncedFetchData]);

  // Загрузка данных сразу при открытии компонента
  useEffect(() => {
    if (filtersStore.selectedGroups.length > 0 && selectedMarkers.length > 0) {
      fetchChartData();
      fetchData({
        pageIndex: 0,
        pageSize: 10,
        sorting: [{ id: 'text_anomality', desc: true }],
      });
    }
  }, []);

// Загрузка данных для графика
const fetchChartData = async () => {
  if (selectedMarkers.length === 0) {
    setChartData([]);
    setChartError(null);
    return;
  }
  setChartLoading(true);
  setChartError(null);
  
  try {
    const params = new URLSearchParams();
    
    // Добавляем параметры фильтрации
    if (filtersStore.selectedGroups && filtersStore.selectedGroups.length > 0) {
      params.append('group_ids', filtersStore.selectedGroups.join(','));
    }
    if (filtersStore.startDate) {
      params.append('start_date', filtersStore.startDate);
    }
    if (filtersStore.endDate) {
      params.append('end_date', filtersStore.endDate);
    }
    if (filtersStore.minLikes) {
      params.append('min_likes', filtersStore.minLikes);
    }
    if (filtersStore.minViews) {
      params.append('min_views', filtersStore.minViews);
    }
    if (filtersStore.minComments) {
      params.append('min_comments', filtersStore.minComments);
    }    
    

    let chartMarkers = selectedMarkers.filter(m => isNumericAnomalityMarker(m));
    params.append('markers', chartMarkers.join(','));

    params.append('period', aggregation === '1' ? 'day' : aggregation === '7' ? 'week' : 'month');
    console.log("PARAMS:", params.toString());

    const response = await axios.get(`${API_BASE_URL}/detection/text_anomality_chart_data`, { params });
    
    const transformedData = response.data.map(item => ({
      ...item, // Сохраняем остальные свойства объекта
      group_id: undefined, // Присваиваем group_id значение marker
      marker: extendedGroupIdToTitle[item.group_id] // Присваиваем marker значение group_id
    }));
    console.log("response: ", response.data)
    console.log("transformedData: ", transformedData)
    setChartData(transformedData || []);
    setChartError(null);
    
  } catch (err) {
    setChartError(err.message);
    setChartData([]);
    console.error('Ошибка загрузки данных графика:', err);
  } finally {
    setChartLoading(false);
  }
};

  // Загрузка данных с пагинацией (для обычного режима)
  const fetchData = async ({ pageIndex, pageSize, sorting }) => {
    if (selectedMarkers.length === 0) {
      setData([]);
      setPageCount(1);
      setTotalRows(0);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const sort = sorting[0] || { id: 'text_anomality', desc: true };
      const page = pageIndex + 1;
             const selectedGroupIds = filtersStore.selectedGroups;
       const { startDate, endDate } = filtersStore.filters;

      // Выбираем все маркеры, которые входят в selectedMarkers или отмечены как isHidden
      const columns = [
        ...selectedMarkers,
        ...Object.keys(anomalityMarkersDictionary).filter(
          key => isAnomalityMarkerHidden(key) && !selectedMarkers.includes(key)
        )
      ];

      const response = await axios.get(`${API_BASE_URL}/detection/text_anomality_analysis`, {
        params: {
        page: page,
        limit: pageSize,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sort_by: sort.id,
        sort_order: sort.desc ? 'desc' : 'asc',
        columns: columns.length > 0 ? columns.join(',') : undefined,
        group_ids: selectedGroupIds.length > 0 ? selectedGroupIds.join(',') : undefined,
        },
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      const total = Number(response.data.total) || 0;
      const size = Number(pageSize) || 10;
      setData([...(response.data.data || [])]);
      setTotalRows(total);
      setPageCount(Math.ceil(total / size) || 1);
    } catch (error) {
      setError(error.response?.data?.error || 'Не удалось загрузить данные');
      setPageCount(1);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка данных графика при изменении фильтров или агрегации
  useEffect(() => {
    if (filtersStore.selectedGroups.length > 0 && viewMode === 'chart') {
      fetchChartData();
    }
  }, [filtersStore.filters, aggregation, viewMode]);

  // Стейт для серверной пагинации
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: pageCount,
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(table.getState().pagination) : updater;
      table.setState((old) => ({
        ...old,
        pagination: newState,
      }));
      if (showTable) {
        fetchData({
          pageIndex: newState.pageIndex,
          pageSize: newState.pageSize,
          sorting: table.getState().sorting,
        });
      }
    },
    onSortingChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(table.getState().sorting) : updater;
      table.setState((old) => ({
        ...old,
        sorting: newState,
      }));
      if (showTable) {
        fetchData({
          pageIndex: table.getState().pagination.pageIndex,
          pageSize: table.getState().pagination.pageSize,
          sorting: newState,
        });
      }
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: 'text_anomality', desc: true }],
    },
  });

  // Проверяем валидность фильтров
  const validationErrors = filtersStore.getValidationErrors();
  const hasValidationErrors = validationErrors.length > 0;

     // Используем MobX реактивность вместо setInterval
   useEffect(() => {
     if (selectedMarkers.length > 0) {
       const {pagination:{ pageIndex, pageSize}, sorting } = table.getState();
       fetchData({ pageIndex, pageSize, sorting });
       if (viewMode === 'chart') {
         fetchChartData();
       }
     }
   }, [filtersStore.selectedGroups, selectedMarkers, table, viewMode]);



  // При переключении режима, если нужных данных нет, грузим их
  useEffect(() => {
    if (viewMode === 'chart' && chartData.length === 0 && selectedMarkers.length > 0) {
      fetchChartData();
    }
    if (viewMode === 'table' && data.length === 0 && selectedMarkers.length > 0) {
      fetchData({
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: table.getState().sorting,
      });
    }
    // eslint-disable-next-line
  }, [viewMode]);


  // При изменении периода агрегации перезагружаем данные графика
  useEffect(() => {
    if (selectedMarkers.length > 0 && viewMode === 'chart') {
      fetchChartData();
    }
  }, [aggregation]);

  // При изменении выбранных маркеров перезагружаем данные графика
  useEffect(() => {
    if (viewMode === 'chart') {
      if (selectedMarkers.length > 0) {
        fetchChartData();
      } else {
        setChartData([]);
        setChartError(null);
      }
    }
  }, [selectedMarkers]);

  const maxDataLength = useMemo(() => 
    Math.max(0, ...chartData.map(s => (Array.isArray(s.data) ? s.data.length : 0))), 
    [chartData]
  );

  const dateRangeText = useMemo(() => {
    if (!chartData.length || !chartData[0].data || chartData[0].data.length === 0) {
      return 'Нет данных';
    }
    const firstSeries = chartData.find(s => Array.isArray(s.data) && s.data.length > 0);
    if (!firstSeries) return 'Нет данных';
    
    const startIndex = Math.min(dataWindow.start, firstSeries.data.length - 1);
    const endIndex = Math.min(dataWindow.end - 1, firstSeries.data.length - 1);
    
    const startDateDt = new Date(firstSeries.data[startIndex]?.date);
    console.log('startDateDt', startDateDt);
    const startDate = !isNaN(startDateDt) ? startDateDt.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }) : formatRuDate(firstSeries.data[startIndex]?.date);
    const endDateDt = new Date(firstSeries.data[endIndex]?.date);
    const endDate = !isNaN(endDateDt) ? endDateDt.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }) : formatRuDate(firstSeries.data[endIndex]?.date);
    
    return `Показаны данные: ${startDate} - ${endDate}`;
  }, [chartData, dataWindow, maxDataLength]);

  const markerColors = useMemo(() => {
    const colors = [
      '#1976d2', // Blue
      '#d32f2f', // Red
      '#388e3c', // Green
      '#f57c00', // Orange
      '#7b1fa2', // Purple
      '#0288d1', // Light Blue
      '#c2185b', // Pink
      '#fbc02d', // Yellow
      '#455a64', // Grey
      '#0097a7', // Cyan
    ];
    const colorMap = {};
    selectedMarkers.forEach((marker, index) => {
      colorMap[marker] = colors[index % colors.length];
      console.log('123')      
    });
    return colorMap;
  }, [selectedMarkers]);

  const fixedMaxY = useMemo(() => {
    if (!chartData.length) return 100; // Запасное значение, если данных нет
    const allValues = chartData
      .filter(s => Array.isArray(s.data))
      .flatMap(s => s.data.map(d => d.value))
      .filter(v => typeof v === 'number' && !isNaN(v));
    const max = Math.max(...allValues);
    return isFinite(max) ? (Math.ceil(max)) : 100; // Увеличиваем на 10% для запаса
  }, [chartData]);

  return (
    <div className="text-anomality-analysis" style={{ display: 'flex', minHeight: '100vh', background: '#fff' }}>
      <div className="main-content-area">

      {/* Блок справки - всегда вверху */}
      <div className="help-info-block" style={{
        background: '#e3f2fd',
        borderRadius: '12px',
        padding: '20px 28px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.07)',
        display: 'block',
        width: '100%'
      }}>
        <h3 style={{ color: '#1976d2', marginTop: 0, marginBottom: 12, fontWeight: 600 }}>
          Справка
        </h3>
        <div>
          <p>На графике показано количество нестандартных дискуссий (в дискуссию входят пост и комментарии к нему) за каждый день выбранного периода. Аномальной считается дискуссия, у которой показатели активности (количество участников, продолжительность и т.п.) сильно отличаются от средних по всем дискуссиям за выбранный период.</p>
          
          <h4 style={{ color: '#1976d2', marginTop: 16, marginBottom: 8, fontWeight: 600 }}>Рекомендация</h4>
          <p>Если в какой-либо день количество аномальных дискуссий сильно отклоняется от остальных дней, рекомендуется просмотреть дискуссии за этот день, выполнить эмотивный и тематический анализ дискуссий за указанный день.</p>
        </div>
      </div>

      {filtersStore.selectedGroups.length === 0 ? (
              <p className="no-data">Выберите сообщества в верхней панели для отображения данных</p>
      ) : (
        <>

        <div className="action-section">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>

          {viewMode === 'chart' && (
            <select value={aggregation} onChange={e => setAggregation(e.target.value)} style={{ fontSize: 16, padding: '6px 12px', borderRadius: 8, border: '1px solid #1976d2', color: '#1976d2', fontWeight: 500 }}>
              <option value='1'>По дням</option>
              <option value='7'>По неделям</option>
              <option value='30'>По месяцам</option>
            </select>
          )}
        </div>

        {/* Кнопка экспорта */}
        {data.length > 0 && (
          <button             
            onClick={() => setShowExportModal(true)}            
            disabled={loading}
            style={{
              padding: "10px 14px",
              background: "#f4f6f8",
              color:  "#222",
              border: "1px solid #d1d5db",            
              borderRadius: 8,              
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 15,
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "#e5e7eb";                
                e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.background = "#f4f6f8";
                e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";
              }
            }}
          >
            Экспорт данных
          </button>
        )}
      </div>
      
      {/* Модальное окно экспорта */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataType="text_anomality"
        title="Экспорт аномальных дискуссий"
      />


        {viewMode === 'chart' && (
          <div style={{width: '100%', minWidth: 1000, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #0001', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {chartLoading ? (
              <p className="loading">Загрузка...</p>
            ) : chartError ? (
              <p className="error">{chartError}</p>
            ) : chartData.length === 0 ? (
              <p className="no-data">Нет данных для отображения</p>
            ) : (
              <>
                <DataWindowSlider
                  aggregation={aggregation}
                  actualDataLength={getActualDataLength(processedChartData, aggregation)}
                />                
                
                <ComparisonChart
                  loading = {loading}
                  chartData={processedChartData}
                  dataWindow={dataWindow}
                  
                  getMarkerLabelFunction={getAnomalityMarkerLabel}
                />
                
              </>
            )}
          </div>
        )}
        {viewMode === 'table' && (
          <>
            <div className="table-scroll-container">
              {loading ? (
                <p className="loading">Загрузка...</p>
              ) : error ? (
                <p className="error">{error}</p>
              ) : data.length === 0 ? (
                <p className="no-data">Нет данных для отображения</p>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              className={header.column.getIsSorted() ? 'sorted' : ''}
                            >
                              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr 
                          key={row.id} 
                          onClick={() => handleRowClick(row.original)}
                          className={row.original.group_id && row.original.post_id ? 'clickable-row' : ''}
                          style={{ cursor: row.original.group_id && row.original.post_id ? 'pointer' : 'default' }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
                    {!loading && !error && data.length > 0 && (
          <div className="pagination">
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
              ««
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              «
            </button>
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              »
            </button>
            <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
              »»
            </button>
            <span>
              Страница <strong>{table.getState().pagination.pageIndex + 1} из {table.getPageCount()}</strong>
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 50].map((size) => (
                <option key={size} value={size}>
                  Показать {size}
                </option>
              ))}
            </select>
          </div>
        )}
        </>
        )}

        {/* Таблица под графиком */}
        {viewMode === 'chart' && (
          <>
            <div className="table-scroll-container" style={{ marginTop: '20px' }}>
              {loading ? (
                <p className="loading">Загрузка таблицы...</p>
              ) : error ? (
                <p className="error">{error}</p>
              ) : data.length === 0 ? (
                <p className="no-data">Нет данных для отображения в таблице</p>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              className={header.column.getIsSorted() ? 'sorted' : ''}
                            >
                              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr 
                          key={row.id} 
                          onClick={() => handleRowClick(row.original)}
                          className={row.original.group_id && row.original.post_id ? 'clickable-row' : ''}
                          style={{ cursor: row.original.group_id && row.original.post_id ? 'pointer' : 'default' }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && !error && data.length > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontWeight: 600, color: "#1976d2", fontSize: 16 }}>
                      Аномальные дискуссии
                    </span>
                    <span style={{ fontWeight: 500, color: "#6b7280", fontSize: 14 }}>
                      Показано {totalRows > 0 ? ((table.getState().pagination.pageIndex * table.getState().pagination.pageSize) + 1) : 0}-{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, totalRows)} из {totalRows} записей
                    </span>
                  </div>
                  <select 
                    value={table.getState().pagination.pageSize} 
                    onChange={(e) => table.setPageSize(Number(e.target.value))}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "1px solid #4a90e2",
                      background: "#fff",
                      fontSize: 14,
                      color: "#374151",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      outline: "none"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#357abd"}
                    onBlur={(e) => e.target.style.borderColor = "#4a90e2"}
                  >
                    {[10, 20, 30, 50].map((size) => (
                      <option key={size} value={size}>Показать {size}</option>
                    ))}
                  </select>
                </div>
                <Pagination
                  currentPage={table.getState().pagination.pageIndex + 1}
                  totalPages={table.getPageCount()}
                  onPageChange={(newPage) => table.setPageIndex(newPage - 1)}
                  maxVisiblePages={5}
                />
              </div>
            )}
          </>
        )}
        </>
      )}

        {/* Модальное окно с деталями поста */}
        <PostDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedRow={selectedRow}
        />
      </div>
    </div>
  );
});

export default TextAnomalityAnalysis;